/**
 * Cross-window live sync for ONE report draft (BroadcastChannel, same origin).
 *
 * Multi-monitor reading opens the same draft in two windows at once: the
 * report page on one monitor and the viewer's DictationHud on another. Each
 * runs its own useReportEditor, so without coordination the two autosaves
 * silently clobber each other's content. This channel carries the per-draft
 * protocol that makes them one editor:
 *
 *  - HELLO / STATE          - presence + 2s heartbeat while peers exist
 *  - SNAPSHOT               - the writer's full content, answered to HELLO
 *  - SECTION_SET(S)         - every local mutation, mirrored live
 *  - INTERIM/ACTIVE_SECTION - live dictation strip + section-focus continuity
 *  - DICTATION_ARMED/...    - one active mic per draft
 *  - DICTATION_STATS        - per-window dictation provenance (signed-with-
 *                             dictation enrichment; see below)
 *  - WRITER_CLAIM/RELEASE   - single-writer lease (epoch-numbered)
 *  - REPORT_CREATED/SAVED   - identity + persistence bookkeeping
 *  - CONTENT_KEYS           - structured keys the SERVER wrote (populate-echo),
 *                             announced to every editor of the draft
 *
 * Kept separate from windowChannel.ts ('newlumen-multi-screen') on purpose:
 * that channel is app-wide navigation/roles; this one is chatty per-draft
 * content and is scoped by `reportKey` so unrelated drafts never cross.
 *
 * The orchestration lives in hooks/use-report-sync.ts. This module owns the
 * wire format, the transport, and the PURE arbitration helpers (writer-lease
 * state machine + per-section revision ordering) so they unit-test without a
 * DOM or a channel.
 */
export function initialLease() {
    return { epoch: 0, holderId: null };
}
/** Take the lease locally (own mutation, takeover, crash recovery). */
export function claimLease(lease, selfId) {
    return { epoch: lease.epoch + 1, holderId: selfId };
}
/**
 * Apply a remote WRITER_CLAIM. Highest (epoch, windowId) wins; an equal-epoch
 * tie goes to the lexicographically higher windowId so every window resolves
 * the race the same way.
 */
export function applyRemoteClaim(lease, claim) {
    if (claim.epoch > lease.epoch)
        return { epoch: claim.epoch, holderId: claim.windowId };
    if (claim.epoch === lease.epoch) {
        if (lease.holderId === null || claim.windowId > lease.holderId) {
            return { epoch: claim.epoch, holderId: claim.windowId };
        }
    }
    return lease;
}
/** Apply a remote WRITER_RELEASE: only the current holder can vacate the lease. */
export function applyRemoteRelease(lease, release) {
    if (lease.holderId !== release.windowId)
        return lease;
    return { epoch: Math.max(lease.epoch, release.epoch), holderId: null };
}
/**
 * Fold a heartbeat into the lease. A STATE claiming writership is an implicit
 * claim (heals joiners that missed the original WRITER_CLAIM); a holder
 * reporting writer:false at or past our epoch has released without us seeing
 * the release.
 */
export function applyRemoteState(lease, state) {
    if (state.writer)
        return applyRemoteClaim(lease, state);
    if (lease.holderId === state.windowId && state.epoch >= lease.epoch) {
        return { epoch: state.epoch, holderId: null };
    }
    return lease;
}
/** Drop a crashed/vanished holder (heartbeat silence past the peer timeout). */
export function dropHolder(lease, windowId) {
    if (lease.holderId !== windowId)
        return lease;
    return { epoch: lease.epoch, holderId: null };
}
/** True when `incoming` should replace the locally recorded tuple. */
export function revBeats(incoming, current) {
    if (!current)
        return true;
    if (incoming.rev !== current.rev)
        return incoming.rev > current.rev;
    return incoming.windowId > current.windowId;
}
/** The tuple a fresh local mutation of this section should carry. */
export function nextRev(current, selfId) {
    return { rev: (current?.rev ?? 0) + 1, windowId: selfId };
}
// ─── Dictation provenance (pure) ───────────────────────────────────────────
//
// Counters are merged per subject window and only ever move forward: a window
// that stops dictating, reconnects, or is relayed by a third window must never
// lower the total the signing window reports.
export const EMPTY_DICTATION_STATS = {
    wordsInserted: 0,
    refineUsed: false,
};
/** Monotonic per-peer merge: max words, sticky refineUsed. */
export function mergeDictationStats(current, incoming) {
    if (!current)
        return { ...incoming };
    return {
        wordsInserted: Math.max(current.wordsInserted, incoming.wordsInserted),
        refineUsed: current.refineUsed || incoming.refineUsed,
    };
}
/** True when the merge actually moved, i.e. it is worth storing/broadcasting. */
export function dictationStatsChanged(current, next) {
    if (!current)
        return true;
    return current.wordsInserted !== next.wordsInserted || current.refineUsed !== next.refineUsed;
}
/** Anything worth putting on the wire (a window that never dictated stays silent). */
export function hasDictation(stats) {
    return stats.wordsInserted > 0 || stats.refineUsed;
}
/**
 * Total the OTHER windows' contributions. Per-peer keys make this a plain sum
 * with no double counting; the caller adds its own local counters.
 */
export function sumRemoteDictationStats(byPeer, selfId) {
    let wordsInserted = 0;
    let refineUsed = false;
    for (const [peerId, stats] of byPeer) {
        if (peerId === selfId)
            continue;
        wordsInserted += stats.wordsInserted;
        refineUsed = refineUsed || stats.refineUsed;
    }
    return { wordsInserted, refineUsed };
}
function makeWindowId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `rsw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
/**
 * Fire-and-forget messaging over BroadcastChannel('newlumen-report-sync').
 *
 * One instance per window (module singleton below); the underlying channel is
 * opened lazily on the first subscription and closed when the last one ends,
 * so a window that never opens a report costs nothing. Self-echo is filtered
 * by windowId, and each subscription is scoped to one reportKey so two
 * different drafts never hear each other.
 */
export class ReportSyncChannel {
    channel = null;
    windowId;
    ctor;
    subscriptions = new Set();
    contentKeyHandlers = new Set();
    onMessage = (event) => {
        const message = event.data;
        if (!message || typeof message !== 'object')
            return;
        if (message.windowId === this.windowId)
            return;
        if (message.type === 'CONTENT_KEYS') {
            for (const handler of [...this.contentKeyHandlers])
                handler(message.reportKey, message.patch);
        }
        for (const sub of this.subscriptions) {
            // Drop messages for a different draft.
            if (sub.reportKey !== message.reportKey)
                continue;
            sub.handler(message);
        }
    };
    open() {
        if (this.ctor && !this.channel) {
            this.channel = new this.ctor('newlumen-report-sync');
            this.channel.addEventListener('message', this.onMessage);
        }
    }
    closeIfIdle() {
        if (this.subscriptions.size > 0 || this.contentKeyHandlers.size > 0 || !this.channel)
            return;
        this.channel.removeEventListener('message', this.onMessage);
        this.channel.close();
        this.channel = null;
    }
    constructor(options) {
        this.windowId = options?.windowId ?? makeWindowId();
        this.ctor =
            options?.broadcastChannelCtor ??
                (typeof BroadcastChannel !== 'undefined'
                    ? BroadcastChannel
                    : null);
    }
    getWindowId() {
        return this.windowId;
    }
    /**
     * Listen for messages addressed to `reportKey`. Returns an unsubscribe
     * function; the transport is torn down when the last subscription ends.
     */
    subscribe(reportKey, handler) {
        this.open();
        const sub = { reportKey, handler };
        this.subscriptions.add(sub);
        return () => {
            this.subscriptions.delete(sub);
            this.closeIfIdle();
        };
    }
    /**
     * Hear CONTENT_KEYS from other windows for ANY draft, editor or not. One
     * per window (the app root) keeps the report caches in step with what the
     * server wrote, so an editor opened later does not hydrate the older rows.
     */
    subscribeContentKeys(handler) {
        this.open();
        this.contentKeyHandlers.add(handler);
        return () => {
            this.contentKeyHandlers.delete(handler);
            this.closeIfIdle();
        };
    }
    /** Post one message for `reportKey`. No-op where BroadcastChannel is unsupported. */
    send(reportKey, message) {
        if (!this.channel)
            return;
        const envelope = {
            ...message,
            reportKey,
            windowId: this.windowId,
            ts: Date.now(),
        };
        this.channel.postMessage(envelope);
    }
    /**
     * Post one message for `reportKey` to every other window AND hand it to this
     * window's own subscribers, which `send` never reaches (self-echo is
     * filtered, and a BroadcastChannel does not deliver to itself). The
     * transport is opened just for this post when nothing in this window is
     * subscribed, so a window with no editor open still reaches the others.
     */
    announce(reportKey, message) {
        const envelope = {
            ...message,
            reportKey,
            windowId: this.windowId,
            ts: Date.now(),
        };
        if (this.channel) {
            this.channel.postMessage(envelope);
        }
        else if (this.ctor) {
            const once = new this.ctor('newlumen-report-sync');
            once.postMessage(envelope);
            once.close();
        }
        for (const sub of [...this.subscriptions]) {
            if (sub.reportKey === reportKey)
                sub.handler(envelope);
        }
    }
}
/** Per-window singleton; hooks share it so one window speaks with one id. */
export const reportSyncChannel = new ReportSyncChannel();
/**
 * Tell every editor of a draft, in this window and the others, that the
 * server has persisted `patch` into its content (see CONTENT_KEYS).
 */
export function announceReportContentKeys(reportKey, patch, channel = reportSyncChannel) {
    if (Object.keys(patch).length === 0)
        return;
    channel.announce(reportKey, { type: 'CONTENT_KEYS', patch });
}
/**
 * Tell every editor of a draft, in this window and the others, that the draft
 * `reportId` was deleted (see REPORT_DELETED). For a surface that deletes a
 * draft without an editor open on it (the worklist row); an open editor
 * announces its own delete through its sync engine instead.
 */
export function announceReportDeleted(reportKey, reportId, channel = reportSyncChannel) {
    channel.announce(reportKey, { type: 'REPORT_DELETED', reportId });
}
/** Canonical reportKey builders, so every surface derives the same key. */
export function standardReportKey(studyId) {
    return `std:${studyId}`;
}
export function tcReportKey(caseId) {
    return `tc:${caseId}`;
}
